import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './aboutWires.css';
import { openQuoteChat, scrollPageTop } from '../../../utils/site';
import MarketingPageLayout from '../../common/layout/MarketingPageLayout';
import FeatureCardsSection from '../../common/marketing/FeatureCardsSection';
import StatsSection from '../../common/marketing/StatsSection';
import ContentCardsSection from '../../common/marketing/ContentCardsSection';
import FaqSectionBlock from '../../common/marketing/FaqSectionBlock';
import CtaSection from '../../common/marketing/CtaSection';
import { getSegmentPageData, getCatalog } from '../../../data/db';
import pageAssets from '../../../data/pageAssets';
import AppImage from '../../common/media/AppImage';
import WiresSeo from './components/WiresSeo';
import WiresHeroSection from './components/WiresHeroSection';
import RouteConstants from '../../../utils/RouteConstants';

const STD_COLORS = {
    'red': '#ef4444', 'yellow': '#eab308', 'blue': '#3b82f6',
    'black': '#1e293b', 'white': '#f8fafc', 'green': '#22c55e'
};

const BRAND_ORDER = ['Nishad', 'Indrason'];

const getProductImage = (product) => {
    if (product.series === 'Ultra Protection Series') return 'indrasonProduct';
    if (product.series === 'Heat Shield Series') return 'heatshieldProduct';
    if (product.series === 'Nishad 2 Core Cable') return 'nishad2Core';
    if (product.series === 'Flexi Series') return 'flexibleCable';
    if (product.series === 'Standard') return 'nishadTwinflat';
    if (product.series === 'Indrason 2 Core Cable') return 'indrason2Core';    
    if (product.series === 'Flexon Series') return 'flexibleCable';
    if (product.series === 'FR Grade Series') return 'indrasonFRMultistand'; 
    return 'wireProduct';
};

const SeriesModule = ({ product, imageKey, isAlt }) => {
    const foundColors = product.colors
        ? Object.keys(STD_COLORS).filter(c => product.colors.toLowerCase().includes(c))
        : [];
    const features = product.highlightFeatures
        ? product.highlightFeatures.split('\n').filter(l => l.startsWith('- ')).slice(0, 6).map(l => l.substring(2).trim())
        : [];

    return (
        <div className={`series-module${isAlt ? ' series-module--alt' : ''}`}>
            <div className="series-module-inner">
                <div className="series-module-sidebar">
                    <span className="series-module-brand">{product.brand}</span>
                    {product.tag && <span className="series-module-tag">{product.tag}</span>}
                    <h3 className="series-module-name">{product.series}</h3>
                    <p className="series-module-type">{product.product}</p>
                    <div className="series-module-image-wrap">
                        <AppImage src={pageAssets[imageKey]} alt={`${product.brand} ${product.product} — ${product.series}`} className="series-module-image" />
                    </div>
                    {foundColors.length > 0 && (
                        <div className="series-module-colors">
                            {foundColors.map(c => (
                                <span key={c} className="series-color-dot" style={{ backgroundColor: STD_COLORS[c] }} title={c}></span>
                            ))}
                            <span className="series-color-label">{product.colors.length > 30 ? 'Multiple Colors' : product.colors}</span>
                        </div>
                    )}
                </div>

                <div className="series-module-main">
                    {features.length > 0 && (
                        <div className="series-module-features">
                            <div className="series-section-label">Key Features</div>
                            <div className="series-features-grid">
                                {features.map((f, i) => (
                                    <div key={i} className="series-feature-chip">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                        <span>{f}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="series-module-pricing">
                        <div className="series-section-label">Available Sizes</div>
                        <div className="series-variants-table">
                            {product.variants.map((v, vi) => {
                                const price = v.sellingPrice || v.mrp;
                                return (
                                    <div key={`${v.variant}-${vi}`} className="series-variant-row">
                                        <span className="series-variant-size">{v.variant}</span>
                                        <span className="series-variant-price">
                                            {price ? `₹${price.toLocaleString('en-IN')}` : '—'}
                                        </span>
                                        {v.coupon ? (
                                            <span className="series-variant-save">Save ₹{v.coupon}</span>
                                        ) : <span />}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <button type="button" className="btn btn-primary series-module-cta" onClick={openQuoteChat}>Get Bulk Quote</button>
                </div>
            </div>
        </div>
    );
};

const AboutWires = () => {
    useEffect(() => {
        scrollPageTop();
    }, []);

    const wireData = getSegmentPageData('wires');
    const catalog = getCatalog('wires');
    const {
        seo,
        hero,
        featuresSection,
        specsSection,
        buyingGuide: buyingGuideSection,
        certifications: certificationsSection,
        faq: faqSection,
        cta: ctaSection,
        structuredProducts,
        families: productFamilies,
        series: wireSeries
    } = wireData;

    return (
        <>
            <WiresSeo
                seo={seo}
                faqItems={faqSection.items}
                structuredProducts={structuredProducts}
                productFamilies={productFamilies}
                buyingGuide={buyingGuideSection}
            />
            <MarketingPageLayout pageClassName="about-wires-page">
                <WiresHeroSection hero={hero} />
                <FeatureCardsSection
                    sectionClassName="about-wires-features"
                    sectionId="features"
                    header={{
                        wrapperClassName: 'about-wires-section-header',
                        subtitleClassName: 'about-wires-section-subtitle',
                        titleClassName: 'about-wires-section-title',
                        subtitle: featuresSection.subtitle,
                        title: featuresSection.title
                    }}
                    gridClassName="about-wires-features-grid"
                    cardClassName="about-wires-feature-card"
                    iconClassName="about-wires-feature-icon"
                    items={featuresSection.items}
                />
                <section id="wire-catalog" className="wire-catalog">
                    <div className="wire-catalog-intro">
                        <div className="container">
                            <div className="about-wires-section-subtitle">Our Products</div>
                            <h2 className="about-wires-section-title">Choose The Right Wire</h2>
                            <p>Browse our complete range — each series engineered for a specific need.</p>
                        </div>
                    </div>

                    {BRAND_ORDER.map((brand) => {
                        const brandProducts = catalog.filter(p => p.brand === brand);
                        if (!brandProducts.length) return null;
                        let moduleIndex = 0;
                        return (
                            <div key={brand} className="wire-brand-group">
                                <div className="container">
                                    <div className="wire-brand-divider">
                                        <span className="wire-brand-label">{brand}</span>
                                    </div>
                                </div>
                                {brandProducts.map((product, idx) => {
                                    const imageKey = getProductImage(product);
                                    const isAlt = moduleIndex % 2 === 1;
                                    moduleIndex++;
                                    return (
                                        <SeriesModule
                                            key={`${brand}-${product.series}-${idx}`}
                                            product={product}
                                            imageKey={imageKey}
                                            isAlt={isAlt}
                                        />
                                    );
                                })}
                            </div>
                        );
                    })}
                </section>
                <StatsSection
                    sectionClassName="about-wires-specs"
                    header={{
                        wrapperClassName: 'about-wires-section-header',
                        subtitleClassName: 'about-wires-section-subtitle',
                        titleClassName: 'about-wires-section-title',
                        subtitle: specsSection.subtitle,
                        title: specsSection.title
                    }}
                    gridClassName="about-wires-specs-grid"
                    cardClassName="about-wires-spec-card"
                    valueClassName="about-wires-spec-value"
                    labelClassName="about-wires-spec-label"
                    items={specsSection.items}
                />
                <ContentCardsSection
                    sectionClassName="about-wires-trust"
                    header={{
                        wrapperClassName: 'about-wires-section-header',
                        subtitleClassName: 'about-wires-section-subtitle',
                        titleClassName: 'about-wires-section-title',
                        subtitle: certificationsSection.subtitle,
                        title: certificationsSection.title
                    }}
                    gridClassName="about-wires-trust-grid"
                    cardClassName="about-wires-trust-card"
                    iconClassName="about-wires-trust-icon"
                    items={certificationsSection.items}
                />
                <ContentCardsSection
                    sectionClassName="about-wires-trust"
                    header={{
                        wrapperClassName: 'about-wires-section-header',
                        subtitleClassName: 'about-wires-section-subtitle',
                        titleClassName: 'about-wires-section-title',
                        descriptionClassName: 'faq-section-desc',
                        subtitle: buyingGuideSection.subtitle,
                        title: buyingGuideSection.title,
                        description: buyingGuideSection.description
                    }}
                    gridClassName="about-wires-trust-grid"
                    cardClassName="about-wires-trust-card"
                    items={buyingGuideSection.items}
                />

                {/* AEO: Wire Size Guide — structured content AI engines extract */}
                <section id="wire-size-guide" className="wire-size-guide">
                    <div className="container">
                        <div className="about-wires-section-header">
                            <div className="about-wires-section-subtitle">Wire Size Guide</div>
                            <h2 className="about-wires-section-title">Which Wire Size for House Wiring in India?</h2>
                            <p className="faq-section-desc">Use this guide to select the correct wire gauge for each circuit in your home or office.</p>
                        </div>

                        {/* Desktop: table | Mobile: cards */}
                        <div className="wire-guide-table-wrap">
                            <table className="wire-guide-table">
                                <thead>
                                    <tr>
                                        <th>Application</th>
                                        <th>Wire Size</th>
                                        <th>Examples</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Lighting Circuits</td>
                                        <td><strong>1.0 – 1.5 sq mm</strong></td>
                                        <td>LED lights, CFL, tube lights, decorative fixtures</td>
                                    </tr>
                                    <tr>
                                        <td>Power Sockets (5A/15A)</td>
                                        <td><strong>2.5 sq mm</strong></td>
                                        <td>TV, computer, fan, phone charger, small appliances</td>
                                    </tr>
                                    <tr>
                                        <td>Heavy Appliances</td>
                                        <td><strong>4.0 sq mm</strong></td>
                                        <td>Air conditioner (1.5 ton), geyser, water heater, induction</td>
                                    </tr>
                                    <tr>
                                        <td>High-Load Circuits</td>
                                        <td><strong>6.0 sq mm</strong></td>
                                        <td>Central AC (2+ ton), kitchen mains, commercial equipment</td>
                                    </tr>
                                    <tr>
                                        <td>Main Supply Line</td>
                                        <td><strong>6.0 – 10.0 sq mm</strong></td>
                                        <td>Meter to distribution board, building mains</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="wire-guide-cards">
                            {[
                                { app: 'Lighting Circuits', size: '1.0 – 1.5 sq mm', examples: 'LED lights, CFL, tube lights, decorative fixtures' },
                                { app: 'Power Sockets (5A/15A)', size: '2.5 sq mm', examples: 'TV, computer, fan, phone charger, small appliances' },
                                { app: 'Heavy Appliances', size: '4.0 sq mm', examples: 'Air conditioner (1.5 ton), geyser, water heater, induction' },
                                { app: 'High-Load Circuits', size: '6.0 sq mm', examples: 'Central AC (2+ ton), kitchen mains, commercial equipment' },
                                { app: 'Main Supply Line', size: '6.0 – 10.0 sq mm', examples: 'Meter to distribution board, building mains' }
                            ].map((row) => (
                                <div key={row.app} className="wire-guide-card">
                                    <div className="wire-guide-card-header">
                                        <span className="wire-guide-card-app">{row.app}</span>
                                        <span className="wire-guide-card-size">{row.size}</span>
                                    </div>
                                    <p className="wire-guide-card-examples">{row.examples}</p>
                                </div>
                            ))}
                        </div>

                        <p className="wire-guide-note">All sizes available in NISHAD UltraGuard (ZHFRLS), Heat Shield (FRLSH) and Indrason ranges. Always use ISI certified (IS-694) fire resistant wire with pure copper conductor.</p>
                    </div>
                </section>

                {/* AEO: Wire Type Comparison — targets "FR vs FRLS vs FRLSH" queries */}
                <section id="wire-types" className="wire-type-comparison">
                    <div className="container">
                        <div className="about-wires-section-header">
                            <div className="about-wires-section-subtitle">Wire Types Explained</div>
                            <h2 className="about-wires-section-title">FR vs FRLS vs FRLSH vs ZHFRLS Wire</h2>
                            <p className="faq-section-desc">Understanding the difference between fire resistant wire grades helps you pick the right safety level.</p>
                        </div>

                        {/* Desktop: table */}
                        <div className="wire-guide-table-wrap">
                            <table className="wire-guide-table wire-comparison-table">
                                <thead>
                                    <tr>
                                        <th>Feature</th>
                                        <th>FR</th>
                                        <th>FRLS</th>
                                        <th>FRLSH</th>
                                        <th>ZHFRLS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Flame Retardant</td>
                                        <td>Yes</td>
                                        <td>Yes</td>
                                        <td>Yes</td>
                                        <td>Yes</td>
                                    </tr>
                                    <tr>
                                        <td>Low Smoke</td>
                                        <td>No</td>
                                        <td>Yes</td>
                                        <td>Yes</td>
                                        <td>Yes</td>
                                    </tr>
                                    <tr>
                                        <td>Low Halogen</td>
                                        <td>No</td>
                                        <td>No</td>
                                        <td>Yes</td>
                                        <td>Zero</td>
                                    </tr>
                                    <tr>
                                        <td>Toxic Gas Emission</td>
                                        <td>High</td>
                                        <td>Medium</td>
                                        <td>Low</td>
                                        <td>Negligible</td>
                                    </tr>
                                    <tr>
                                        <td>Best For</td>
                                        <td>Basic wiring</td>
                                        <td>Residential</td>
                                        <td>Homes & offices</td>
                                        <td>Hospitals, malls, high-rise</td>
                                    </tr>
                                    <tr>
                                        <td>NISHAD Range</td>
                                        <td>—</td>
                                        <td>—</td>
                                        <td>Heat Shield, Indrason</td>
                                        <td>UltraGuard</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile: cards per wire type */}
                        <div className="wire-compare-cards">
                            {[
                                { type: 'FR', label: 'Flame Retardant', smoke: 'No', halogen: 'No', toxic: 'High', bestFor: 'Basic wiring', nishad: null },
                                { type: 'FRLS', label: 'Flame Retardant Low Smoke', smoke: 'Yes', halogen: 'No', toxic: 'Medium', bestFor: 'Residential', nishad: null },
                                { type: 'FRLSH', label: 'FR Low Smoke Halogen', smoke: 'Yes', halogen: 'Yes', toxic: 'Low', bestFor: 'Homes & offices', nishad: 'Heat Shield, Indrason' },
                                { type: 'ZHFRLS', label: 'Zero Halogen FRLS', smoke: 'Yes', halogen: 'Zero', toxic: 'Negligible', bestFor: 'Hospitals, malls, high-rise', nishad: 'UltraGuard', featured: true }
                            ].map((wire) => (
                                <div key={wire.type} className={`wire-compare-card${wire.featured ? ' wire-compare-card--featured' : ''}`}>
                                    <div className="wire-compare-card-header">
                                        <span className="wire-compare-card-type">{wire.type}</span>
                                        <span className="wire-compare-card-label">{wire.label}</span>
                                    </div>
                                    <div className="wire-compare-card-body">
                                        <div className="wire-compare-row">
                                            <span>Low Smoke</span>
                                            <span className={wire.smoke === 'Yes' ? 'val-yes' : 'val-no'}>{wire.smoke}</span>
                                        </div>
                                        <div className="wire-compare-row">
                                            <span>Low Halogen</span>
                                            <span className={wire.halogen === 'No' ? 'val-no' : 'val-yes'}>{wire.halogen}</span>
                                        </div>
                                        <div className="wire-compare-row">
                                            <span>Toxic Gas</span>
                                            <span>{wire.toxic}</span>
                                        </div>
                                        <div className="wire-compare-row">
                                            <span>Best For</span>
                                            <span>{wire.bestFor}</span>
                                        </div>
                                        {wire.nishad && (
                                            <div className="wire-compare-row wire-compare-row--brand">
                                                <span>NISHAD Range</span>
                                                <span>{wire.nishad}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <FaqSectionBlock
                    sectionClassName="faq-section"
                    sectionId="faq"
                    header={{
                        wrapperClassName: 'about-wires-section-header',
                        subtitleClassName: 'about-wires-section-subtitle',
                        titleClassName: 'about-wires-section-title',
                        descriptionClassName: 'faq-section-desc',
                        subtitle: faqSection.subtitle,
                        title: faqSection.title,
                        description: faqSection.description
                    }}
                    faqProps={{
                        items: faqSection.items,
                        listClassName: 'faq-list',
                        itemClassName: 'faq-item',
                        questionClassName: 'faq-question',
                        questionTextClassName: 'faq-question-text',
                        iconClassName: 'faq-icon',
                        answerClassName: 'faq-answer'
                    }}
                />
                <section className="about-wires-cross-link">
                    <div className="container">
                        <p>Also explore our <Link to={RouteConstants.FANS} className="cross-link-anchor">Ceiling Fans</Link> range — high-speed models with copper winding, decorative finishes and 1200mm sweep.</p>
                    </div>
                </section>
                <CtaSection
                    sectionClassName="about-wires-cta"
                    title={ctaSection.title}
                    description={ctaSection.description}
                    buttonClassName="about-wires-cta-btn"
                    buttonLabel={ctaSection.buttonLabel}
                    onClick={openQuoteChat}
                />
            </MarketingPageLayout>
        </>
    );
};

export default AboutWires;
